import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, X, Sparkles, ArrowLeft, Save, Paperclip } from 'lucide-react';
import { Textarea } from './ui/textarea';
import ReactMarkdown from 'react-markdown';
import supabase from '../supabase';
import { useWorkspace } from '../context/workspace-context';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import { CHAT_ROLE } from '../constants/chat-roles';
import { ChatMessage } from '../interfaces/chat-message';
import {
  MAX_FILE_SIZE,
  ALLOWED_FILE_TYPES,
} from '../constants/document-upload';
import { parseDocuments } from '../helpers/parse-documents';
import type { Level } from '../hooks/use-course';
import type { ProposedLevel } from '../interfaces/proposed-level';

interface CourseDepartmentRow {
  departments: { name: string } | null;
}

interface CreateLevelProps {
  courseId: number;
  existingLevels: Level[];
  onClose: () => void;
  onLevelCreated: (levelData: ProposedLevel) => void;
  hideHeader?: boolean;
}

const processAIMessage = (message: string) => {
  return message.replace(/Level ID: \d+/g, 'Level');
};

export default function CreateLevel({
  courseId,
  existingLevels,
  onClose,
  onLevelCreated,
  hideHeader = false,
}: CreateLevelProps) {
  const { currentWorkspace } = useWorkspace();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [proposedLevel, setProposedLevel] = useState<ProposedLevel | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-adjust textarea height
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'inherit';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(scrollHeight, 120)}px`; // Max 5 lines (approximately)
    }
  }, [input]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Set initial message when component mounts
  useEffect(() => {
    const nextLevelNumber =
      existingLevels.length > 0
        ? Math.max(...existingLevels.map((l) => l.level)) + 1
        : 1;

    const initialMessage = `I'll help you create Level ${nextLevelNumber} for this course. Let's discuss:

     1. What topics should this level cover?
     2. What difficulty level should it be (beginner, intermediate, advanced)?
     3. Do you want it to build on previous levels or cover new material?

     Please provide as much detail as possible to create a relevant and appropriate level.`;

    setMessages([{ role: CHAT_ROLE.ASSISTANT, content: initialMessage }]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    const currentFile = attachedFile;
    setInput('');
    setIsLoading(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'inherit';
    }

    setMessages((prev) => [
      ...prev,
      {
        role: CHAT_ROLE.USER,
        content: userMessage,
        fileName: currentFile?.name,
      },
    ]);

    try {
      // Get course details for context
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select(
          `
          id,
          name,
          description,
          purpose,
          subject_matter,
          status,
          course_departments (
            departments (
              name
            )
          )
        `,
        )
        .eq('id', courseId)
        .single();

      if (courseError) throw courseError;

      // Get organization details from current workspace
      let orgData = null;
      if (currentWorkspace) {
        const { data, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', currentWorkspace.id)
          .single();

        if (!orgError) {
          orgData = data;
        }
      }

      // Format existing levels for context
      const formattedLevels = existingLevels.map((level) => ({
        id: level.id,
        level: level.level,
        name: level.name,
        description: level.description,
        difficulty: level.difficulty,
        topics: level.topics,
      }));

      const departmentRows =
        courseData.course_departments as unknown as CourseDepartmentRow[];

      const departments = departmentRows
        .map((row) => row.departments?.name)
        .filter(Boolean);

      const { course_departments, ...courseFields } = courseData;

      // Parse attached document if present
      let documentContent: string | undefined;
      if (attachedFile) {
        const parsed = await parseDocuments([attachedFile]);
        documentContent = parsed[0]?.content;
        setAttachedFile(null);
      }

      // Send message to backend for level creation assistance
      const response = await apiFetch(ENDPOINTS.LEVEL_CREATION, {
        method: 'POST',
        body: JSON.stringify({
          conversationId,
          message: userMessage,
          existingLevels: formattedLevels,
          courseDetails: { ...courseFields, departments },
          organizationDetails: orgData,
          documentContent,
        }),
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();

      // Store conversationId for subsequent messages
      if (data.conversationId) {
        setConversationId(data.conversationId);
      }

      // Add the AI response to the chat
      setMessages((prev) => [
        ...prev,
        { role: CHAT_ROLE.ASSISTANT, content: data.conversation },
      ]);

      // If the AI has proposed a level, store it
      if (data.levelData) {
        setProposedLevel(data.levelData);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: CHAT_ROLE.ASSISTANT,
          content: 'Sorry, I encountered an error. Please try again.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileAttach = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setMessages((prev) => [
        ...prev,
        {
          role: CHAT_ROLE.ASSISTANT,
          content:
            'Invalid file type. Please upload a .txt, .doc, .docx, or .pdf file.',
        },
      ]);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setMessages((prev) => [
        ...prev,
        {
          role: CHAT_ROLE.ASSISTANT,
          content: `"${file.name}" exceeds the 10MB file size limit.`,
        },
      ]);
      return;
    }

    setAttachedFile(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSaveLevel = async () => {
    if (!proposedLevel) return;

    setIsSaving(true);
    try {
      // Call the onLevelCreated callback with the proposed level data
      await onLevelCreated(proposedLevel);

      // Add a confirmation message
      setMessages((prev) => [
        ...prev,
        {
          role: CHAT_ROLE.ASSISTANT,
          content: 'Level successfully created and saved to the database!',
        },
      ]);

      // Clear the proposed level after saving
      setProposedLevel(null);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: CHAT_ROLE.ASSISTANT,
          content:
            'Sorry, I encountered an error while saving the level. Please try again.',
        },
      ]);
    } finally {
      setIsSaving(false);
    }
  };

  const renderMessage = (message: ChatMessage) => {
    if (message.role === CHAT_ROLE.SYSTEM) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted px-3 py-1.5 rounded-full">
          <Sparkles className="h-3.5 w-3.5" />
          {message.content}
          <Sparkles className="h-3.5 w-3.5" />
        </div>
      );
    }

    const content =
      message.role === CHAT_ROLE.ASSISTANT
        ? processAIMessage(message.content)
        : message.content;

    return (
      <div
        className={`max-w-[80%] rounded-lg p-3 ${
          message.role === CHAT_ROLE.ASSISTANT
            ? 'bg-[#FDF8F3] prose prose-sm dark:prose-invert'
            : 'bg-[#A78BFA] text-white'
        }`}
      >
        {message.role === CHAT_ROLE.ASSISTANT ? (
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
              ul: ({ children }) => (
                <ul className="mb-4 list-disc pl-4 last:mb-0">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="mb-4 list-decimal pl-4 last:mb-0">{children}</ol>
              ),
              li: ({ children }) => (
                <li className="mb-1 last:mb-0">{children}</li>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold">{children}</strong>
              ),
              em: ({ children }) => <em className="italic">{children}</em>,
              h1: ({ children }) => (
                <h1 className="text-lg font-bold mb-4 last:mb-0">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-base font-bold mb-3 last:mb-0">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-sm font-bold mb-2 last:mb-0">{children}</h3>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        ) : (
          <>
            {message.fileName && (
              <div className="flex items-center gap-1.5 mb-1.5 text-white/80 text-xs">
                <Paperclip className="h-3 w-3" />
                {message.fileName}
              </div>
            )}
            {content}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header - only show if hideHeader is false */}
      {!hideHeader && (
        <div className="p-4 border-b flex items-center justify-between bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h3 className="font-semibold">Create New Level</h3>
              <p className="text-sm text-muted-foreground">
                Tell me about the level you want to add
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Chat Area */}
      <ScrollArea className="flex-grow p-4">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.role === CHAT_ROLE.SYSTEM
                  ? 'justify-center'
                  : message.role === CHAT_ROLE.ASSISTANT
                    ? 'justify-start'
                    : 'justify-end'
              }`}
            >
              {renderMessage(message)}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Save Level Button (Appears when level is proposed) */}
        {proposedLevel && (
          <div className="mt-6 flex justify-center">
            <Button
              onClick={handleSaveLevel}
              className="gap-2 bg-green-600 hover:bg-green-700 text-white"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving Level...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Level
                </>
              )}
            </Button>
          </div>
        )}
      </ScrollArea>

      {/* Input Area */}
      <div className="p-4 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        {attachedFile && (
          <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-gray-100 rounded-md text-sm text-gray-700">
            <Paperclip className="h-3.5 w-3.5 text-gray-500" />
            <span className="truncate">{attachedFile.name}</span>
            <button
              type="button"
              onClick={() => setAttachedFile(null)}
              className="ml-auto text-gray-500 hover:text-gray-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileAttach}
            accept=".txt,.doc,.docx,.pdf"
            className="hidden"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="self-end shrink-0"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setInput(e.target.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Type your message..."
            disabled={isLoading}
            className="min-h-[40px] max-h-[120px] resize-none"
            rows={1}
          />
          <Button
            onClick={handleSendMessage}
            disabled={isLoading || !input.trim()}
            className="self-end bg-[#A78BFA] text-white hover:bg-[#A78BFA]"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
}
